import { useMutation, useQueryClient } from '@tanstack/react-query';

import type { ReshapedUser } from '$src/types';
import type { CheckoutV2 } from '@getjust/api-schema';

import { USER_INFO_QUERY_KEY } from '$src/constants';
import { justAPI } from '$src/http';

const ADD_ADDRESS_MUTATION_KEY = 'MUTATION/ADD_ADDRESS';

export const useAddAddress = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (address: CheckoutV2.CreateProfileAddressRequestBody) =>
      justAPI.post<CheckoutV2.CreateProfileAddressResponseBody>('/v2/profile/address', address),
    mutationKey: [ADD_ADDRESS_MUTATION_KEY],
    onMutate: async (address): Promise<{ previousUser: ReshapedUser | undefined }> => {
      await queryClient.cancelQueries({ queryKey: [USER_INFO_QUERY_KEY] });
      const previousUser = queryClient.getQueryData<ReshapedUser>([USER_INFO_QUERY_KEY]);
      queryClient.setQueryData<ReshapedUser>([USER_INFO_QUERY_KEY], (oldUser): ReshapedUser | undefined => {
        if (!oldUser) return undefined;

        const copyAddresses = [...(oldUser.addresses ?? [])];
        copyAddresses.forEach((address) => {
          address.is_default_shipping = false;
          address.is_default_billing = false;
        });

        copyAddresses.push({
          ...address.address,
          is_default_billing: address.is_default_billing,
          is_default_shipping: address.is_default_shipping,
          id: 'optimisticUpdateAdd',
        });
        return {
          ...oldUser,
          addresses: copyAddresses,
        };
      });
      return {
        previousUser,
      };
    },
    onError: (err, newAddress, context) => {
      queryClient.setQueryData<ReshapedUser>([USER_INFO_QUERY_KEY], context?.previousUser);
    },
    // Always refetch after error or success:
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: [USER_INFO_QUERY_KEY] });
    },
  });
};
